<template>
	<div class="stats-card-wrapper" v-loading="loading">
		<div class="stats-card-row" v-for="(row, rowIndex) in cardRows" :key="rowIndex">
			<div class="stats-card" v-for="(card, index) in row" :key="index">
				<div class="card-header">
					<div class="card-content">
						<div class="card-title">{{ card.title }}</div>
						<div class="card-value" :style="{ color: card.valueColor || '#1e293b' }">
							{{ card.value }}
							<span class="card-unit" v-if="card.unit">{{ card.unit }}</span>
						</div>
					</div>
					<div class="card-icon" :style="{ background: card.iconBg }">
						<i :class="card.icon" :style="{ color: card.iconColor || '#2563eb' }"></i>
					</div>
				</div>
				<div class="card-sub" v-if="card.subtext" :class="card.trendType || 'normal'">
					<template v-if="card.showTrendIcon !== false && card.trendType === 'up'">
						<i class="el-icon-top"></i>
						{{ card.subtext }}
					</template>
					<template v-else-if="card.showTrendIcon !== false && card.trendType === 'down'">
						<i class="el-icon-bottom"></i>
						{{ card.subtext }}
					</template>
					<template v-else>
						{{ card.subtext }}
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { apiGetPressureRegulatorMonitorOverview } from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'StatsCardRow',
	props: {
		timeRange: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			loading: false,
			overview: {},
		}
	},
	computed: {
		cardRows() {
			return [this.regulatorCards, this.collectorCards]
		},
		regulatorCards() {
			return [
				{
					title: '调压器总数',
					value: this.formatNumber(this.overview.regulatorTotalCount),
					unit: '个',
					subtext: this.formatRateText(this.overview.regulatorGrowthRate, '较上月', 'suffix'),
					trendType: this.getTrendType(this.overview.regulatorGrowthRate),
					iconBg: '#dbeafe',
					iconColor: '#2563eb',
					icon: 'el-icon-s-data',
				},
				{
					title: '正常运行',
					value: this.formatNumber(this.overview.regulatorNormalCount),
					unit: '个',
					valueColor: '#16a34a',
					subtext: this.formatRateText(this.overview.regulatorRunningRate, '运行率'),
					trendType: this.getTrendType(this.overview.regulatorRunningRate),
					showTrendIcon: false,
					iconBg: '#dcfce7',
					iconColor: '#16a34a',
					icon: 'el-icon-circle-check',
				},
				{
					title: '异常告警',
					value: this.formatNumber(this.overview.regulatorAbnormalCount),
					unit: '个',
					valueColor: '#ca8a04',
					trendType: 'normal',
					iconBg: '#fef9c3',
					iconColor: '#ca8a04',
					icon: 'el-icon-warning-outline',
				},
				{
					title: '设备故障告警',
					value: this.formatNumber(this.overview.regulatorFaultCount),
					unit: '条',
					valueColor: '#dc2626',
					subtext: this.formatRateText(this.overview.regulatorFaultRate, '故障率'),
					trendType: this.getTrendType(this.overview.regulatorFaultRate),
					showTrendIcon: false,
					iconBg: '#fee2e2',
					iconColor: '#dc2626',
					icon: 'el-icon-warning',
				},
			]
		},
		collectorCards() {
			return [
				{
					title: '采集器总数',
					value: this.formatNumber(this.overview.collectorTotalCount),
					unit: '个',
					subtext: this.formatRateText(this.overview.collectorOnlineRate, '在线率'),
					trendType: this.getTrendType(this.overview.collectorOnlineRate),
					showTrendIcon: false,
					iconBg: '#f3e8ff',
					iconColor: '#9333ea',
					icon: 'el-icon-connection',
				},
				{
					title: '正常运行',
					value: this.formatNumber(this.overview.collectorNormalCount),
					unit: '个',
					valueColor: '#16a34a',
					subtext: this.formatRateText(this.overview.collectorOnlineRate, '在线率'),
					trendType: this.getTrendType(this.overview.collectorOnlineRate),
					showTrendIcon: false,
					iconBg: '#dcfce7',
					iconColor: '#16a34a',
					icon: 'el-icon-circle-check',
				},
				{
					title: '今日告警数',
					value: this.formatNumber(this.overview.todayAlarmCount),
					unit: '条',
					valueColor: '#ca8a04',
					subtext: this.formatRateText(this.overview.alarmGrowthRate, '较昨日', 'suffix'),
					trendType: this.getTrendType(this.overview.alarmGrowthRate),
					iconBg: '#fef9c3',
					iconColor: '#ca8a04',
					icon: 'el-icon-bell',
				},
				{
					title: '设备故障告警',
					value: this.formatNumber(this.overview.faultAlarmCount),
					unit: '条',
					valueColor: '#dc2626',
					iconBg: '#fee2e2',
					iconColor: '#dc2626',
					icon: 'el-icon-warning',
				},
			]
		},
	},
	watch: {
		timeRange: {
			handler() {
				this.getOverview()
			},
			immediate: true,
		},
	},
	methods: {
		getOverview() {
			this.loading = true
			apiGetPressureRegulatorMonitorOverview(this.getTimeParams())
				.then(res => {
					this.overview = res || {}
				})
				.catch(() => {
					this.overview = {}
				})
				.finally(() => {
					this.loading = false
				})
		},
		getTimeParams() {
			const [startTime] = this.timeRange || []
			return {
				startTime: startTime ? this.dayjs(startTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
			}
		},
		formatNumber(value) {
			return value === null || value === undefined || value === '' ? '--' : value
		},
		formatRateText(value, label, labelPosition = 'prefix') {
			if (value === null || value === undefined || value === '') return `${label} --`
			return labelPosition === 'suffix' ? `${value}% ${label}` : `${label} ${value}%`
		},
		getTrendType(value) {
			const trendValue = Number.parseFloat(value)
			if (Number.isNaN(trendValue)) return 'normal'
			if (trendValue > 0) return 'up'
			if (trendValue < 0) return 'down'
			return 'normal'
		},
	},
}
</script>

<style lang="scss" scoped>
.stats-card-wrapper {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.stats-card-row {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
}
.stats-card {
	flex: 1;
	min-width: 0;
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	box-shadow: none;
	backdrop-filter: none;
}
.card-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	width: 100%;
	min-height: 64px;
	gap: 16px;
}
.card-content {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
}
.card-icon {
	width: 40px;
	height: 40px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	i {
		font-size: 20px;
		line-height: 1;
	}
}
.card-title {
	font-size: 14px;
	line-height: 20px;
	color: #64748b;
}
.card-value {
	font-size: 32px;
	font-weight: 700;
	line-height: 40px;
	color: #1e293b;

	.card-unit {
		font-size: 14px;
		font-weight: 400;
		color: #64748b;
		margin-left: 2px;
	}
}
.card-sub {
	font-size: 12px;
	line-height: 16px;
	display: flex;
	align-items: center;
	gap: 4px;

	&.up {
		color: #16a34a;
	}
	&.down {
		color: #dc2626;
	}
	&.normal {
		color: #64748b;
	}

	i {
		font-size: 16px;
	}
}

@media (max-width: 1240px) {
	.stats-card {
		flex: 1 1 calc(50% - 6px);
	}
}

@media (max-width: 900px) {
	.stats-card {
		flex-basis: 100%;
	}
}
</style>
